import { useMemo } from 'react'
import Group from 'components/utils/Group'
import WalledContent from 'components/Common/WalledContent'
import OfferTileLoggedInButtons from 'components/OfferList/OfferListTiles/Support/OfferTileLoggedInButtons'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LEOfferForceBundlePricing from './LEOfferPricing/LEOfferForceBundlePricing'
import LEOfferPricing from './LEOfferPricing/LEOfferPricing'
import { unique } from 'lib/array/arrayUtils'
import { ViewOfferButton } from './ViewOfferButton'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'

export type LEOfferPriceDetailsOffer = App.HotelOffer | App.HotelOfferSummary | App.TourOffer | App.TourOfferSummary | App.VillaOffer | App.VillaOfferSummary

export type LEOfferPricingComponentProps = {
  offer: LEOfferPriceDetailsOffer
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filters?: App.OfferListFilters
}

export type LEOfferPricingComponent = React.ComponentType<LEOfferPricingComponentProps>

interface Props {
  offer: LEOfferPriceDetailsOffer
  variant?: 'default' | 'condensed'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filters?: App.OfferListFilters
  PricingComponent?: LEOfferPricingComponent
  CtaComponent?: React.ComponentType
}

function LEOfferPriceDetails(props: Props) {
  const {
    offer,
    variant = 'default',
    selectedDatesAndRoomsFilter,
    filters,
    PricingComponent,
    CtaComponent,
  } = props

  const wall = <OfferTileLoggedInButtons
    align="right"
    fullWidth={false}
    discountLabelAlign="end"
  />
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  // Points carousels must never use force-bundle cash (hotel+flight) pricing — it bypasses
  // burn UI and often flashes ¥0 / $0 while flights resolve.
  const shouldUseForceBundlePricing = !filters?.viewPriceInPoints
    && !!(offer.bundledWithFlightsOnly || charterBundleRoute)
  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName
  return (
    <WalledContent enforced={offer.walledGarden} wall={wall}>
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <VerticalSpacer gap={8}>
            <div>
              {shouldUseForceBundlePricing && <LEOfferForceBundlePricing
                offer={offer}
                selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
              />}
              {!shouldUseForceBundlePricing && (PricingComponent
                ? <PricingComponent offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} />
                : <LEOfferPricing offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} />)}
            </div>
            <PriceRowAgentHubCommission
              offerId={offer.id}
              offerLocations={offerLocations}
              vendorName={vendorName}
              productType={offer.productType}
            />
          </VerticalSpacer>
          <ProductPaletteProvider product={offer}>
            {CtaComponent ? <CtaComponent /> : <ViewOfferButton variant={variant} />}
          </ProductPaletteProvider>
        </Group>
      </Group>
    </WalledContent>
  )
}

export default LEOfferPriceDetails
